import { createSlice } from '@reduxjs/toolkit';
import { NOTIFICATION_FILTERS } from '../../common/notificationTypes';

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState: {
        loading: false,
        loadingMore: false,
        items: [],
        unreadCount: 0,
        hasMore: false,
        nextCursor: null,
        filter: NOTIFICATION_FILTERS.unread,
        preferences: null,
    },
    reducers: {
        setNotificationsLoading: (state, { payload }) => {
            state.loading = payload;
        },
        setNotificationsLoadingMore: (state, { payload }) => {
            state.loadingMore = payload;
        },
        setNotificationsFilter: (state, { payload }) => {
            if (
                payload !== NOTIFICATION_FILTERS.read
                && payload !== NOTIFICATION_FILTERS.unread
            ) {
                return;
            }
            if (state.filter === payload) return;

            state.filter = payload;
            state.items = [];
            state.hasMore = false;
            state.nextCursor = null;
            state.loading = true;
        },
        setNotifications: (state, { payload }) => {
            state.items = payload.notifications || [];
            if (typeof payload.unreadCount === 'number') {
                state.unreadCount = payload.unreadCount;
            }
            state.hasMore = Boolean(payload.hasMore);
            state.nextCursor = payload.nextCursor || null;
            if (payload.filter === NOTIFICATION_FILTERS.read
                || payload.filter === NOTIFICATION_FILTERS.unread) {
                state.filter = payload.filter;
            }
        },
        appendNotifications: (state, { payload }) => {
            const known = new Set(state.items.map((n) => String(n._id)));
            const incoming = (payload.notifications || [])
                .filter((n) => !known.has(String(n._id)));

            state.items = [...state.items, ...incoming];
            state.hasMore = Boolean(payload.hasMore);
            state.nextCursor = payload.nextCursor || null;
            if (typeof payload.unreadCount === 'number') {
                state.unreadCount = payload.unreadCount;
            }
        },
        addNotification: (state, { payload }) => {
            // El socket puede reenviar la misma notificación (reconexión, doble listener)
            const exists = state.items.some((n) => String(n._id) === String(payload._id));
            if (exists) return;

            if (state.filter === NOTIFICATION_FILTERS.unread && !payload.read) {
                state.items.unshift(payload);
            }

            if (!payload.read) {
                state.unreadCount += 1;
            }
        },
        markNotificationRead: (state, { payload }) => {
            const index = state.items.findIndex((n) => n._id === payload);
            if (index === -1) {
                state.unreadCount = Math.max(0, state.unreadCount - 1);
                return;
            }

            const notification = state.items[index];
            if (!notification.read) {
                notification.read = true;
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }

            // En «No leídas» sale de la lista al marcarse.
            if (state.filter === NOTIFICATION_FILTERS.unread) {
                state.items.splice(index, 1);
            }
        },
        markAllNotificationsRead: (state) => {
            state.items.forEach((n) => {
                n.read = true;
            });
            state.unreadCount = 0;
            if (state.filter === NOTIFICATION_FILTERS.unread) {
                state.items = [];
                state.hasMore = false;
                state.nextCursor = null;
            }
        },
        setNotificationPreferences: (state, { payload }) => {
            state.preferences = payload;
        },
    },
});

export const {
    setNotificationsLoading,
    setNotificationsLoadingMore,
    setNotificationsFilter,
    setNotifications,
    appendNotifications,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    setNotificationPreferences,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
